import React, { useCallback, useEffect, useState } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'

/**
 * AML / PEP screening rail — same provider checks as Watchlist & Investigation
 * (`POST /api/aml/checks/run`), with results in a right-side drawer.
 */
export default function EcddAmlPepPanel({
  memberId,
  memberName,
  reason = 'ECDD re-screen',
  onOpenOsintScreening,
  showOsintButton = false,
}) {
  const [amlChecks, setAmlChecks] = useState([])
  const [amlLoading, setAmlLoading] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [error, setError] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerData, setDrawerData] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const loadAmlChecks = useCallback(async () => {
    if (!memberId) {
      setAmlChecks([])
      return
    }
    setLoadingHistory(true)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=8`)
      )
      const json = await res.json().catch(() => ({}))
      if (!json.success) throw new Error(json.error || 'Failed to load AML history')
      setAmlChecks(Array.isArray(json.data) ? json.data : [])
    } catch (e) {
      setError(e.message || 'Failed to load AML history')
      setAmlChecks([])
    } finally {
      setLoadingHistory(false)
    }
  }, [memberId])

  useEffect(() => {
    loadAmlChecks()
  }, [loadAmlChecks])

  const openDrawer = (data) => {
    setDrawerData(data)
    setSelectedIndex(0)
    setDrawerOpen(true)
  }

  const handleReScreen = async () => {
    const name = String(memberName || '').trim()
    if (!memberId || !name) {
      setError('Member ID and name are required to run AML/PEP screening.')
      return
    }
    setAmlLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl('/api/aml/checks/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberId,
          name,
          country: 'AUST',
          reason,
          performed_by: 'web_user',
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || 'AML check failed')
      await loadAmlChecks()
      const d = json.data
      if (d?.id) {
        openDrawer({
          checkId: d.id,
          name: d.name || name,
          country: d.country,
          results: Array.isArray(d.sample) ? d.sample : [],
        })
      }
    } catch (e) {
      setError(e?.message || 'Re-screen failed')
    } finally {
      setAmlLoading(false)
    }
  }

  const viewAmlCheckDetails = async (checkId) => {
    if (!checkId) return
    setAmlLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(checkId)}`))
      const json = await res.json().catch(() => ({}))
      if (!json?.success) throw new Error(json?.error || 'Failed to load check')
      const d = json.data || {}
      const sample = Array.isArray(d.result_sample)
        ? d.result_sample
        : Array.isArray(d.results)
          ? d.results
          : Array.isArray(d.sample)
            ? d.sample
            : []
      openDrawer({
        checkId: d.id || checkId,
        name: d.full_name || d.name || memberName,
        country: d.country_code || d.country,
        results: sample,
      })
    } catch (e) {
      setError(e?.message || 'Failed to open check')
    } finally {
      setAmlLoading(false)
    }
  }

  const latest = amlChecks[0]
  const latestMatches = Number(latest?.result_count) || 0
  const results = drawerData?.results || []
  const selected = results[selectedIndex] || {}

  return (
    <>
      <aside className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#0A516915] flex items-center justify-center shrink-0">
              <ShieldCheckIcon className="w-5 h-5 text-psg-teal" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-[#073B4D]">AML / PEP Screening</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">
                Same provider screen as Watchlist & Investigation
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3.5 space-y-3">
          {!memberId ? (
            <p className="text-sm text-slate-500 m-0">No member linked — screening needs a patron ID.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status</div>
                  {loadingHistory ? (
                    <div className="text-sm text-slate-500 mt-0.5">Loading…</div>
                  ) : latest ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {latestMatches > 0 ? (
                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 shrink-0" />
                      ) : (
                        <CheckCircleIcon className="w-4 h-4 text-green-600 shrink-0" />
                      )}
                      <span className={`text-sm font-semibold ${latestMatches > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                        {latestMatches > 0 ? `${latestMatches} for review` : 'Clear'}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 mt-0.5">No screening on record</div>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="bg-psg-teal hover:bg-psg-teal-700 shrink-0"
                  disabled={amlLoading || !memberName}
                  onClick={handleReScreen}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                  {amlLoading ? 'Screening…' : 'Re-screen'}
                </Button>
              </div>

              {latest && (
                <div className="text-xs text-slate-500">
                  SCR-{latest.id}
                  {latest.created_at
                    ? ` · ${new Date(latest.created_at).toLocaleString('en-AU')}`
                    : ''}
                </div>
              )}

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  Screening history
                </div>
                {amlChecks.length === 0 ? (
                  <p className="text-xs text-slate-500 m-0">
                    No checks yet — click Re-screen to run AML/PEP.
                  </p>
                ) : (
                  <ul className="space-y-2 m-0 p-0 list-none">
                    {amlChecks.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-start gap-2 rounded-lg border border-slate-100 px-2.5 py-2"
                      >
                        {Number(c.result_count) > 0 ? (
                          <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                        ) : (
                          <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-slate-700">
                            SCR-{c.id} · {c.result_count || 0} match{(c.result_count || 0) !== 1 ? 'es' : ''}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {c.reason || 'Screening'}
                            {c.created_at
                              ? ` · ${new Date(c.created_at).toLocaleDateString('en-AU')}`
                              : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-xs font-semibold text-psg-teal hover:underline inline-flex items-center gap-0.5 shrink-0 bg-transparent border-none p-0 cursor-pointer"
                          onClick={() => viewAmlCheckDetails(c.id)}
                        >
                          <EyeIcon className="w-3.5 h-3.5" /> View
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {showOsintButton && typeof onOpenOsintScreening === 'function' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
                  onClick={onOpenOsintScreening}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                  OSINT screening sources
                </Button>
              )}
            </>
          )}

          {error && <p className="text-xs text-red-600 m-0">{error}</p>}
        </div>
      </aside>

      {drawerOpen && drawerData && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-[880px] bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between py-3.5 px-4 border-b border-slate-200 shrink-0">
              <div className="min-w-0">
                <div className="text-base font-bold text-slate-800 truncate">
                  {selected.primaryName || selected.name || drawerData.name}
                </div>
                <div className="text-xs text-slate-500">
                  Check #{drawerData.checkId}
                  {drawerData.country ? ` · ${drawerData.country}` : ''}
                  {' · AML / PEP'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-3">
                  {results.length === 0 ? (
                    <div className="rounded-lg p-3.5 border border-green-200 bg-green-50">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="text-sm font-bold text-green-700">Clear — No matches</div>
                          <div className="text-xs text-slate-600">
                            Screening returned zero PEP, sanctions, or adverse media matches for{' '}
                            <strong>{drawerData.name}</strong>.
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg p-3.5 border border-amber-200 bg-amber-50">
                        <div className="text-xs font-bold text-slate-800 mb-2">Risk Assessment</div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <div className="inline-block px-3 py-1.5 text-xs font-bold rounded-md text-white bg-amber-600">
                              {selected.riskLevel || 'High Risk'}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">Overall Risk</div>
                          </div>
                          <div>
                            <div className="inline-block px-3 py-1.5 text-xs font-semibold rounded-md bg-violet-50 text-violet-700 border border-violet-200">
                              {selected.threatLevel || '—'}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">Threat</div>
                          </div>
                          <div>
                            <div className="inline-block px-3 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 border border-red-200">
                              {selected.riskCategory || 'PEP'}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">Category</div>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg p-3.5 border border-slate-200">
                        <div className="text-xs font-bold text-slate-800 mb-2">Match detail</div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            Confidence:{' '}
                            <span className="font-bold text-red-600">
                              {typeof selected.confidence !== 'undefined' ? `${selected.confidence}%` : '—'}
                            </span>
                          </div>
                          {selected.nationality && <div>Nationality: {selected.nationality}</div>}
                          {selected.gender && <div>Gender: {selected.gender}</div>}
                        </div>
                        {selected.profileNotes && (
                          <div className="mt-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                            {selected.profileNotes}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 mb-2">Matches ({results.length})</div>
                  {results.length === 0 ? (
                    <div className="p-4 rounded-lg text-center bg-green-50 border border-green-200">
                      <CheckCircleIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <div className="text-xs font-semibold text-green-700">Zero matches</div>
                    </div>
                  ) : (
                    results.map((res, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedIndex(idx)}
                        className={`w-full text-left rounded-lg p-3 mb-2 border cursor-pointer transition-colors ${
                          idx === selectedIndex
                            ? 'border-psg-teal bg-[#0A516908]'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="flex justify-between mb-1 gap-2">
                          <span className="font-semibold text-slate-800 text-sm truncate">
                            {res.primaryName || res.name || 'Match'}
                          </span>
                          {typeof res.confidence !== 'undefined' && (
                            <span className="text-xs text-slate-500 shrink-0">{res.confidence}%</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">{res.riskCategory || ''}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
